import React, { Component, createContext } from "react";

import { auth } from "../../firebase";
import { storeValue, getStoredValue, userAddressKey } from '../storage/storage-utils';
import { fetchUserInfo } from '../registration/registration-utils';
import { fetchMetaData } from '../dispensaries/dispensary-utils';
import { getCartItems, restoreItemDetails } from '../checkout/order-utils';
import { connect } from 'react-redux';
import { setDeliveryAddress, setDispensaries, setFeeStructure, restoreCart } from '../actions/cartActions';
import { trackEvent } from "../analytics/tracking";
import { executeSessionRestoreQueue, executeSessionReadyQueue } from './provider-utils';
import { logWithIntercom } from '../intercom/intercom-utils';
import PropTypes from 'prop-types';

export const UserContext = createContext({ user: null });

/**
 *  There should always be a real signed-in user or an "anonymous" user so
 *  we have a token to pass with API requests.      
 * 
 *  Anonymous users will be prompted for their Zip Code and blocked from checkout.
 * 
 *  Note: API calls should generally be wrapped in queueForSessionReady otherwise
 *  they may fail if session hasn't been created yet
 */
class UserProvider extends Component {
  
  state = {
    user: null,
    location: null,
    searchLocation: null,    /* e.g. from address prompt */
  };

  // Let's keep track of the session start/refresh time
  sessionStartMs = React.createRef();

  abortController = new AbortController();

  /**
   * We set the master list of dispensaries (in redux) on session start/update
   * 
   * This allows us to promote dispensary-specific products and retrieve dispensary 
   * details without making API calls everywhere   
   */
  dispensariesCallback = (response) => {
    if (response.data.length) {
      this.props.setDispensaries(response.data);
    }
  }; 
 
  /**
   * We set the fee structure (in redux) on session start/update
   */ 
  feeStructureCallback = (response) => {
    if (response.order_fees) {
      const { cannabis_tax_percent, sales_tax_percent, delivery_fee_cents, delivery_service_fee_percent, pickup_fee_cents, pickup_service_fee_percent, in_store_payment_fee_percent } = response.order_fees;
      this.props.setFeeStructure(cannabis_tax_percent, sales_tax_percent, delivery_fee_cents, delivery_service_fee_percent, pickup_fee_cents, pickup_service_fee_percent, in_store_payment_fee_percent);
    } else {
      trackEvent('error_meta_data', 'order_fees data not returned by API');
    }
  };

  /**
   * We set the delivery address for registered users on session start/update
   */ 
  deliveryAddressCallback = (response) => {
    console.log('setting user location');
    if (response.location) {
      // legacy redux
      this.props.setDeliveryAddress(response.location, response.tel_number, response.name); 
      this.setState({ location: response.location });
    } 
  };
  
  /**
   * Restore cart from previous session
   * 
   * @param {object} response - api response 
   */
  restoreCartCallback = (response) => {
    if (response.dispensary_id && response.items?.length) {
      const { dispensary_id, items, order_type } = response;
      this.props.restoreCart(dispensary_id, restoreItemDetails(items), order_type);
    }
  }

  addUserStateBodyAttribute = (user) => {
    try {
      if (user && !user.isAnonymous) {
        document.body.setAttribute('data-session', 'user');
      } else {
        document.body.setAttribute('data-session', 'anonymous')
      }
    } catch(e) {
      // continue 
    }  
  };
       
  componentDidMount = async () => {
    try {
      // Iframed Incognito will fail on local setting!
      // if ( window.top === window.self) {
        auth.setPersistence('local'); /* versus session */
      //} else {
      //  auth.setPersistence('none');
      //}

      auth.onAuthStateChanged(async user => {
        
        // Update session start time
        const currentMs = new Date().getTime();
        if (this.sessionStartMs.current) {
          const minutes = (currentMs - this.sessionStartMs.current)/60000;
          console.log(`user session updated after ${parseFloat(minutes.toString()).toFixed(2)} minutes`);  
        }  
        this.sessionStartMs.current = currentMs;
        
        // If user is null, create an anonymous user so we can make API requests
        if ( user === null ) {
          // TODO: Fix the timeout hack (lost session on page refresh )
          window.setTimeout(() => {
            if (auth.currentUser === null) {
              console.log('creating anonymous user');
              this.createAnonymousUser(() => { this.setState({ 
                user: auth.currentUser,
                location: null 
              }); });
            }
          } , 1000);
        } else {
          console.log('user session state updated');
          this.setState({ user });
          
          // execute any API requests that are waiting for the user to be set/created
          executeSessionReadyQueue(user);
          
          // these are requests for real-user data like "Your Account"
          if (!user.isAnonymous) {
            executeSessionRestoreQueue(user);     
            // Add registered user to Intercom dashboard
            if (user.displayName) {
              const userCreationTS = new Date(user.metadata?.creationTime).getTime()/1000;
              const actionObj = {
                Registration_Complete: true
              };
              logWithIntercom(user.displayName, user.email, userCreationTS, actionObj);
            }
          }
        }
        
        /**
         *  This is where we fetch and load most of the data we need into redux
         */
        // On session start/update: fetch dispensary data (redux)
        // ( Removed after adding DispensariesProvider ) 
        //  if ( this.props.dispensaries.length === 0) {
        //    fetchDispensaries(this.dispensariesCallback, this.abortController); 
        //  }
        
        // Fetch pricing metadata (redux)
        if (this.props.sales_tax_percent === 0) {
          fetchMetaData(this.feeStructureCallback, this.abortController);
        }
        
        /**
         * Fetch the user's stored cart items
         * FIXME: Using a delay to allow time for product fetch/load
         */
        if (user && !user.isAnonymous && !this.props.addedItems.length) {
          window.setTimeout(() => {
            getCartItems(this.restoreCartCallback, this.abortController); 
          }, 2000);
        }

        // Fetch the Logged-In user's delivery address/tel number
        if (user && !user.isAnonymous && !this.props.street_address) {
          fetchUserInfo(this.deliveryAddressCallback, this.abortController);
        }

        // For some components (footer, etc.) we'll use a body attribute to enable/disable content
        this.addUserStateBodyAttribute(user);
  
        // Check storage for location
        if(!this.state.searchLocation) {
          console.log('checking storage for search location');
          const searchLocation = getStoredValue(userAddressKey);
          if (searchLocation) {
            console.log('storing search location');
            this.setState({
              searchLocation: JSON.parse(searchLocation)
            });
          }
        } 

      });
    } catch (error) {
      trackEvent('error_auth_state', error); 
    }
  };

  componentDidUpdate = async () => {
    if (auth.currentUser === null) {
      // TODO: Fix the timeout hack (lost session on page refresh )
      window.setTimeout(() => {
        if (auth.currentUser === null) {
          console.log('creating anonymous user');
          this.createAnonymousUser(() => { this.setState({ user: auth.currentUser }); });
        }
      } , 1000);
    }
  }  

  componentWillUnmount(){
    this.abortController.abort();
  }

  createAnonymousUser = (onCompleteFn) => {
    auth.signInAnonymously()
      .then(data => {
        onCompleteFn();
      })
      .catch(function(error) {
        /** 
         * const errorCode = error.code;
         * const errorMessage = error.message;
         * if (errorCode === 'auth/operation-not-allowed') {
         *   alert('You must enable Anonymous auth in the Firebase Console.');
         * }
         */
        if (window.location.host.indexOf('localhost') > -1) {
          alert('unexpected anonymous user creation error');
        }
        trackEvent('error_create_anon_user', error.code);
        console.error(error);
      });
  };
  
  /**
   * Set searchLocation based on a user's address input
   * 
   * The registration-based location is preferred over the searchLocation
   * 
   * @param {object} searchLocation 
   */
  setSearchLocation = (searchLocation) => {
    try {
      if (!this.state.searchLocation ||
           JSON.stringify(searchLocation) !== JSON.stringify(this.state.searchLocation)) {
        this.setState({searchLocation});
        // The app will reload on marketing site click-through from iframe, so storage is needed
        storeValue(userAddressKey, JSON.stringify(searchLocation));
      }
    } catch(e) {
      trackEvent('error_setting_search_location');
    }
  }

  render() {
    return (
      <UserContext.Provider value={{
        user: this.state.user,
        location: this.state.location,
        searchLocation: this.state.searchLocation,
        setSearchLocation: this.setSearchLocation
      }}>
        {this.props.children}
      </UserContext.Provider>
    );
  }
}

UserProvider.propTypes = {
  dispensaries: PropTypes.array,
  cannabis_tax_percent: PropTypes.number,
  sales_tax_percent: PropTypes.number,
  street_address: PropTypes.string,
  setDeliveryAddress: PropTypes.func,
  setDispensaries: PropTypes.func,
  setFeeStructure: PropTypes.func
};

const mapStateToProps = (state) => {
  return {
    dispensaries: state.dispensaries,
    street_address: state.delivery_address.street_address,
    sales_tax_percent: state.sales_tax_percent,
    addedItems: state.addedItems
  }
}

const mapDispatchToProps = (dispatch)=>{
  return {
    setDeliveryAddress: (delivery_address, tel_number, user_name) => {dispatch(setDeliveryAddress(delivery_address, tel_number, user_name))},
    setDispensaries: (dispensaries) => {dispatch(setDispensaries(dispensaries))},
    restoreCart: (dispensary_id, items, order_type) => {dispatch(restoreCart(dispensary_id, items, order_type))},
    setFeeStructure: (
      cannabis_tax_percent,
      sales_tax_percent, 
      delivery_fee_cents, 
      delivery_service_fee_percent, 
      pickup_fee_cents, 
      pickup_service_fee_percent,
      pickup_in_store_payment_fee_percent) => {
        dispatch(setFeeStructure(
          cannabis_tax_percent,
          sales_tax_percent, 
          delivery_fee_cents, 
          delivery_service_fee_percent, 
          pickup_fee_cents, 
          pickup_service_fee_percent,
          pickup_in_store_payment_fee_percent)
        )
      } 
  }
}
 
export default connect(mapStateToProps,mapDispatchToProps)(UserProvider);